import { OutcomesForParty, OutcomeSummary } from "./Analytics.ts";
import { Stage } from "./CaseConfiguration.ts";
import { Case, StoredResults } from "./CaseModel.ts";
import { AdjustedParty, AdjustedResults } from "./settlementAdjusted.ts";

/** An outcome as a page reads it; with settlements it may be one or stem from a world. */
export type ShownOutcome = OutcomeSummary & {
  // the party left the case by settlement at this stage
  settledAt?: Stage;
  world?: string;
};

/** A party's outcomes with the settlements the user expects, as pages read outcomes. */
export function adjustedOutcomes(
  litigation: OutcomesForParty<OutcomeSummary>,
  { outcomes, settlement, average }: AdjustedParty,
): OutcomesForParty<ShownOutcome> {
  const evOf = (stage: Stage) =>
    average.stageEvs.find((row) => row.stage === stage)?.ev;
  return {
    ...litigation,
    outcomes: [
      ...outcomes,
      ...(settlement?.points ?? []).map(
        ({ stage, probability, netResult, value, lawFirm, world }) => ({
          uuid: `settled-${world}`,
          scenario: { won: [], lost: [] },
          probability,
          issuesWon: [],
          issuesLost: [],
          value,
          netResult,
          enforcementVariants: [],
          lawFirm,
          settledAt: stage,
          world,
        }),
      ),
    ],
    average: {
      ...litigation.average,
      party: average.party,
      barrister: average.barrister,
      lawFirm: average.lawFirm,
      stageResults: litigation.average.stageResults.map((row) => ({
        ...row,
        ev: { ...row.ev, party: evOf(row.stage) ?? row.ev.party },
      })),
    },
  };
}

/** The expected values, a party's own and those against each opponent. */
export function adjustedEvs(
  evs: StoredResults["evs"],
  parties: AdjustedParty[],
): StoredResults["evs"] {
  return Object.fromEntries(
    Object.entries(evs).map(([partyId, ofParty]) => {
      const party = parties.find((p) => p.partyId === partyId);
      if (!party) return [partyId, ofParty];
      return [
        partyId,
        {
          ...ofParty,
          own: ofParty.own.map((row) => ({
            ...row,
            value:
              party.average.stageEvs.find(({ stage }) => stage === row.stage)
                ?.ev ?? row.value,
          })),
          vsOthers: Object.fromEntries(
            Object.entries(ofParty.vsOthers).map(([opponentId, rows]) => [
              opponentId,
              rows.map((row) => ({
                ...row,
                value:
                  party.average.stageEvsVs[opponentId]?.find(
                    ({ stage }) => stage === row.stage,
                  )?.ev ?? row.value,
              })),
            ]),
          ),
        },
      ];
    }),
  );
}

const doneOf = (c: Case): AdjustedResults | undefined =>
  c.settlementAdjusted?.status === "done" ? c.settlementAdjusted : undefined;

/** The case as the analytics read it with the switch on adjusted: litigated where nothing is. */
export function adjustedCase(c: Case): Case {
  const adjusted = doneOf(c);
  if (!adjusted || !c.results) return c;
  const { parties } = adjusted;
  const of = (id: string) => parties.find(({ partyId }) => partyId === id);
  const { results } = c;
  return {
    ...c,
    results: {
      ...results,
      evs: adjustedEvs(results.evs, parties),
      outcomes: results.outcomes.map((outcome) => {
        const party = of(outcome.partyId);
        return party ? adjustedOutcomes(outcome, party) : outcome;
      }),
      partyStats: results.partyStats?.map((ofParty) => {
        const party = of(ofParty.partyId);
        return party
          ? {
              ...ofParty,
              stats: {
                ...ofParty.stats,
                ...party.stats,
                ev: party.average.stageEvs[0]?.ev ?? ofParty.stats.ev,
              },
            }
          : ofParty;
      }),
      ...(adjusted.revenueData && { revenueData: adjusted.revenueData }),
    },
  };
}
